import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Hammer, ArrowLeft, Check } from 'lucide-react';
import { useAmendMode, AmendBanner } from './useAmendMode';
import './NativeInventoryAdjustmentNew.css';

// Assembly Build (Native) — build finished goods without NetSuite in the loop.
// Pick an assembly, quantity, and location; the preview shows every BOM
// component's requirement vs native on-hand and the estimated roll-up cost.
// Posting: FIFO component costing + FG cost layer, ledger quantity movement,
// asset↔asset JE, best-effort NetSuite assemblyBuild (echo-deduped).
// Reuses the adjustment screen's stylesheet (same house template).

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};
const newRef = () => `BLD-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function NativeAssemblyBuildNew() {
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [itemQuery, setItemQuery] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [item, setItem] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [memo, setMemo] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [writesEnabled, setWritesEnabled] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);      // selected WO (or null = unlinked)
  const [openWos, setOpenWos] = useState([]);            // released/in_progress WOs for the chosen assembly
  const sourceKey = useRef(newRef());

  const woRemaining = (wo) => {
    const r = Number(wo.quantity) - Number(wo.quantity_built || 0);
    return r > 0 ? r : 0;
  };

  useEffect(() => {
    axios.get('/api/native-inventory-adjustments/locations')
      .then(({ data }) => setLocations((data.locations || []).filter((l) => l.is_active !== false)))
      .catch(() => setLocations([]));
    axios.get('/api/native-assembly-builds')
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
  }, []);

  // Prefill from an existing build — Make Copy (?copyFrom=) and Amend
  // (?amendOf=) load the same fields; only the memo and the submit target
  // differ, since an amendment IS the original document, corrected.
  const [searchParams] = useSearchParams();
  const amend = useAmendMode();
  const [amendOfNumber, setAmendOfNumber] = useState(null);
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    const from = copyFrom || amend.amendOf;
    if (!from || locations.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-assembly-builds/${encodeURIComponent(from)}`);
        if (cancelled || !data.build) return;
        const b = data.build;
        const loc = locations.find((l) => l.id === b.location_id);
        if (loc) setLocation(loc);
        setQuantity(String(Number(b.quantity)));
        if (copyFrom) setMemo(`Copy of ${b.build_number}`);
        else { setMemo(b.memo || ''); setAmendOfNumber(b.build_number); }
        if (b.sku) {
          const { data: it } = await axios.get(`/api/native-items/${encodeURIComponent(b.sku)}`);
          if (it.item && !cancelled) setItem({ id: it.item.id, sku: it.item.sku, name: it.item.name });
        }
        // An amend of a WO-linked build keeps the link (the void un-rolls the
        // WO; the corrected repost rolls it back up).
        if (b.native_work_order_id && !copyFrom) {
          try {
            const { data: woData } = await axios.get(`/api/native-production/${encodeURIComponent(b.native_work_order_id)}`);
            if (woData.workOrder && !cancelled) setWorkOrder(woData.workOrder);
          } catch { /* link is optional; the operator can re-pick */ }
        }
      } catch { /* prefill is best-effort; the form stays blank */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Deep link (?workOrderId=): prefill the whole form from the work order —
  // assembly, remaining quantity, and the link itself.
  useEffect(() => {
    const woId = searchParams.get('workOrderId');
    if (!woId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-production/${encodeURIComponent(woId)}`);
        if (cancelled || !data.workOrder) return;
        const wo = data.workOrder;
        setWorkOrder(wo);
        setQuantity(String(woRemaining(wo) || Number(wo.quantity)));
        if (wo.assembly_item_id) {
          const { data: it } = await axios.get(`/api/native-items/${encodeURIComponent(wo.assembly_item_id)}`);
          if (it.item && !cancelled) setItem({ id: it.item.id, sku: it.item.sku, name: it.item.name });
        }
      } catch { /* the operator can still pick everything by hand */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open WOs for the chosen assembly (released / in_progress — the committed
  // ones a build may post against).
  useEffect(() => {
    setOpenWos([]);
    if (!item) { setWorkOrder(null); return undefined; }
    let cancelled = false;
    axios.get('/api/native-production', { params: { assemblyItemId: item.id, limit: 50 } })
      .then(({ data }) => {
        if (cancelled) return;
        const open = (data.workOrders || []).filter((w) => ['released', 'in_progress'].includes(w.status) && woRemaining(w) > 0);
        setOpenWos(open);
        // Keep a deep-linked selection; drop a selection that no longer matches.
        setWorkOrder((cur) => (cur && cur.assembly_item_id === item.id ? cur : null));
      })
      .catch(() => { if (!cancelled) setOpenWos([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Assembly search (debounced) — assemblies only.
  useEffect(() => {
    if (itemQuery.trim().length < 2 || item) { setItemResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-items', { params: { q: itemQuery.trim(), pageSize: 15, type: 'Assembly' } });
        setItemResults((data.items || []).filter((i) => /assembly/i.test(i.itemType || i.item_type || '')));
      } catch {
        setItemResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [itemQuery, item]);

  // Preview whenever the trio is complete.
  useEffect(() => {
    setPreview(null);
    if (!item || !location || !Number(quantity) || Number(quantity) <= 0) return undefined;
    let cancelled = false;
    setPreviewing(true);
    setError(null);
    axios.get('/api/native-assembly-builds/preview', {
      params: { item: item.id, quantity: Number(quantity), locationId: location.id, workOrderId: workOrder?.id || undefined },
    })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
  }, [item, location, quantity, workOrder]);

  const post = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(amend.postUrl('/api/native-assembly-builds'), {
        sourceKey: sourceKey.current,
        item: item.id,
        quantity: Number(quantity),
        location: { id: location.id },
        memo: memo.trim() || undefined,
        workOrderId: workOrder?.id || undefined,
        ...amend.bodyExtras(),
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      sourceKey.current = newRef(); // a failed post gets a fresh idempotency key
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const b = confirmation.build;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{b.build_number} posted</h2>
          <div className="nia-confirm-grid">
            <span>Built</span><strong>{qty(b.quantity)} × {b.sku}</strong>
            <span>Location</span><strong>{location?.name}</strong>
            {confirmation.workOrder && confirmation.workOrder.ok && (
              <>
                <span>Work order</span>
                <strong>
                  {confirmation.workOrder.woNumber} — {qty(confirmation.workOrder.quantityBuilt)} of {qty(confirmation.workOrder.quantity)} built
                  {confirmation.workOrder.status === 'built' ? ' (complete)' : ''}
                </strong>
              </>
            )}
            {confirmation.workOrder && !confirmation.workOrder.ok && (
              <>
                <span>Work order</span>
                <strong>roll-up failed ({confirmation.workOrder.error}) — the build itself posted; update the WO by hand or repost later</strong>
              </>
            )}
            <span>Component cost</span><strong>{money(b.total_component_cost)}</strong>
            <span>FG unit cost</span><strong>{money(b.fg_unit_cost)}</strong>
            <span>Quantity leg</span><strong>{b.movement_status}</strong>
            <span>Costing</span><strong>{b.costing_status}{b.costing_error ? ` (${b.costing_error})` : ''}</strong>
            <span>GL</span><strong>{b.gl_status}</strong>
            <span>NetSuite</span><strong>{b.ns_status}{b.ns_transaction_id ? ` (${b.ns_transaction_id})` : ''}</strong>
          </div>
          <p className="nia-note">{confirmation.netsuiteNote}</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>New build</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Hammer size={20} /> {amend.isAmend ? 'Amend Assembly Build' : 'Assembly Build (Native)'}</h1>
        {writesEnabled === false && (
          <div className="nia-banner">Posting is disabled (NATIVE_ASSEMBLY_BUILDS_ENABLED is off on the Run service). Preview works; posting will 403.</div>
        )}
      </div>

      <AmendBanner amend={amend} docLabel={amendOfNumber} what="the build" />

      <div className="nia-card">
        <h3>1. Location</h3>
        <div className="nia-locations">
          {locations.map((l) => (
            <button
              key={l.id}
              className={`nia-location ${location?.id === l.id ? 'selected' : ''}`}
              onClick={() => setLocation(l)}
            >
              {l.name}
            </button>
          ))}
        </div>
      </div>

      <div className="nia-card">
        <h3>2. Assembly + quantity</h3>
        {!item ? (
          <>
            <input
              className="nia-input nia-input-search"
              placeholder="Search assemblies by SKU or name…"
              value={itemQuery}
              onChange={(e) => setItemQuery(e.target.value)}
            />
            {itemResults.length > 0 && (
              <div className="nia-results">
                {itemResults.map((r) => (
                  <button key={r.id} className="nia-result" onClick={() => { setItem(r); setItemQuery(''); setItemResults([]); }}>
                    <code>{r.sku}</code> {r.name}
                    <span className="nia-result-muted">on hand {qty(r.onHand)}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            <code>{item.sku}</code> {item.name}
            <button className="nia-icon-btn" onClick={() => { setItem(null); setPreview(null); }}>×</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Quantity to build
            <input className="nia-input nia-input-num" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
            Memo (optional)
            <input className="nia-input" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
          </label>
        </div>
      </div>

      {item && openWos.length > 0 && (
        <div className="nia-card">
          <h3>Work order (optional)</h3>
          <p className="nia-hint" style={{ marginTop: 0 }}>
            Build against a released work order: components consume the WO's BOM snapshot, the built
            quantity rolls up, and the WO closes itself when fully built.
          </p>
          <div className="nia-locations">
            <button
              className={`nia-location ${!workOrder ? 'selected' : ''}`}
              onClick={() => setWorkOrder(null)}
            >
              No work order
            </button>
            {openWos.map((w) => (
              <button
                key={w.id}
                className={`nia-location ${workOrder?.id === w.id ? 'selected' : ''}`}
                onClick={() => { setWorkOrder(w); setQuantity(String(woRemaining(w))); }}
              >
                {w.wo_number} — {qty(woRemaining(w))} remaining{w.customer_name ? ` (${w.customer_name})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {previewing && <div className="nia-card"><div className="nia-hint">Computing component requirements…</div></div>}
      {error && <div className="nia-error">{error}</div>}

      {preview && (
        <div className="nia-card">
          <h3>3. Components — {qty(preview.quantity)} × {preview.item.sku}</h3>
          {preview.workOrder && (
            <p className="nia-hint" style={{ marginTop: 0 }}>
              Against <strong>{preview.workOrder.woNumber}</strong> — recipe is the work order's BOM snapshot;{' '}
              {qty(preview.workOrder.remaining)} of {qty(preview.workOrder.quantity)} remaining to build.
            </p>
          )}
          <table className="nia-table">
            <thead>
              <tr>
                <th>Component</th>
                <th className="r">Required</th>
                <th className="r">On hand here</th>
                <th className="r">Est. unit cost</th>
                <th className="r">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {preview.components.map((c) => (
                <tr key={c.itemId} className={c.short > 0 ? 'nia-short' : ''}>
                  <td><code>{c.sku}</code> {c.name}</td>
                  <td className="r">{qty(c.qtyRequired)}</td>
                  <td className="r">
                    {qty(c.onHand)}
                    {c.short > 0 && <span className="nia-cost-source nia-cost-none"> short {qty(c.short)}</span>}
                  </td>
                  <td className="r">{money(c.estUnitCost)}</td>
                  <td className="r">{money(c.estCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nia-footer">
            <div className="nia-footer-totals">
              <span>Total component cost (est.) <strong>{money(preview.totalEstCost)}</strong></span>
              <span>FG unit cost (est.) <strong>{money(preview.estFgUnitCost)}</strong></span>
              {preview.shortages > 0 && (
                <span className="nia-cost-source nia-cost-none">
                  {preview.shortages} component{preview.shortages === 1 ? '' : 's'} short — posting still allowed (components go negative, like NetSuite)
                </span>
              )}
            </div>
            <div className="nia-actions">
              <button
                className="nia-btn nia-btn-primary"
                onClick={post}
                disabled={submitting || writesEnabled === false || (amend.isAmend && !amend.amendReasonValid)}
              >
                {submitting ? 'Posting…' : amend.isAmend ? 'Reverse and repost' : 'Post build'}
              </button>
            </div>
          </div>
          <p className="nia-hint" style={{ marginTop: 8 }}>
            Actual costing at post time consumes FIFO cost layers (the preview uses average cost). The finished good
            gets one new layer at the rolled-up cost; the JE moves value between inventory asset accounts; NetSuite
            gets a matching assembly build when reachable.
          </p>
        </div>
      )}
    </div>
  );
}
